import React from 'react';
import { range } from 'lodash';
import { FormControl, Select, MenuItem, Box } from '@benchlabs/react-design-system';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';

/**
 * Styled container for the MonthYearSelect component.
 * Easier to add CSS here than inline styles.
 */
const MonthYearContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  label {
    font-size: 14px;
    line-height: 30px;
    margin: 11px 5px 9px 0;
    padding: 2px 2px 1px 2px;
    color: ${colors.dust};
  }

  .md-select-value {
    border-bottom-style: none;
  }
`;

/**
 * Props for MonthYearSelect component.
 */
type MonthYearSelectProps = {
  /**
   * Accessible label for the control; describes what the selection represents.
   */
  label: string;

  /**
   * The currently selected date in 'YYYY-MM' format (for example, "2025-04").
   */
  date: string;

  /**
   * When true, the control is non-interactive and visually presented as disabled.
   */
  disabled?: boolean;

  /**
   * When true, indicates the selected date refers to a historic period (may affect styling, validation, or available options).
   */
  isHistoric?: boolean;

  /**
   * When true, enforces that a value must be selected (null/empty values are not permitted).
   */
  notNull?: boolean;

  /**
   * Callback fired when the selected date changes. Receives three string arguments:
   *   - date: the full 'YYYY-MM' string,
   *   - year: the four-digit year string ('YYYY'),
   *   - month: the two-digit month string ('MM').
   */
  onDateChange: (date: string, year: string, month: string) => void;
};

/**
 * MonthYearSelect component for selecting month and year at the top of the dashboard.
 * @param props The component props.
 * @returns JSX.Element
 */
const MonthYearSelect: React.FC<MonthYearSelectProps> = ({
  label,
  date,
  disabled = false,
  isHistoric = false,
  notNull = false,
  onDateChange,
}) => {
  const [year, month] = date.split('-');
  const allowNull = !notNull;

  const menuProps = {
    MenuListProps: {
      style: {
        display: 'flex',
        flexDirection: 'column',
      } as React.CSSProperties,
    },
  };

  return (
    <MonthYearContainer>
      <label>{label}:</label>

      {/* Month select */}
      <FormControl variant="outlined" size="small" disabled={disabled}>
        <Select
          value={month}
          onChange={e => {
            const value = e.target.value;
            const selectedDate = year && value ? `${year}-${value}` : '';
            onDateChange(selectedDate, year, value);
          }}
          displayEmpty
          aria-label={label + ' Month'}
          data-testid="bk-month-year-select-month"
          className="md-select-value"
          MenuProps={menuProps}
        >
          {allowNull && <MenuItem value=""></MenuItem>}
          {Array.from({ length: 12 }, (_, i) => {
            const monthNumber = String(i + 1).padStart(2, '0');
            const monthName = new Date(0, i).toLocaleString('default', { month: 'long' });
            return (
              <MenuItem key={monthNumber} value={monthNumber}>
                {monthName}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* Year select */}
      <FormControl variant="outlined" size="small" disabled={disabled}>
        <Select
          value={year}
          onChange={e => {
            const value = e.target.value;
            const selectedDate = value && month ? `${value}-${month}` : '';
            onDateChange(selectedDate, value, month);
          }}
          displayEmpty
          aria-label={label + ' Year'}
          data-testid="bk-month-year-select-year"
          className="md-select-value"
          MenuProps={menuProps}
        >
          {allowNull && <MenuItem value=""></MenuItem>}
          {getYears(isHistoric).map(year => (
            <MenuItem key={year} value={String(year)}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </MonthYearContainer>
  );
};

export default MonthYearSelect;

/**
 * Generates an array of years based on whether historic years are needed.
 * @param isHistoric - Whether to include historic years.
 * @returns Array of years.
 */
export function getYears(isHistoric = false): number[] {
  const date = new Date();
  const currentYear = date.getFullYear();
  const firstActiveYear = 2000;

  if (isHistoric) {
    return range(currentYear, currentYear - 14);
  }

  return range(firstActiveYear, currentYear + 7);
}
